<template>
  <Wrapper :custom-breadcrumb="customBreadcrumb">
    <LoadingScreen :isActive="getLoading || getLoadingGetDetail">
      <component-guard :requiredPermissions="['28.1']" redirectUrl="/home">
        <div class="form-rent-adjustment-container">
          <div class="content">
            <!-- SECTION - Title -->
            <div class="title-section">
              <div class="d-flex align-items-center" style="gap: 8px">
                <h1>{{ pageTitle }}</h1>
                <StatusBadge
                  v-if="transferUnitStatus"
                  :statusText="mapStatus.status"
                  :statusClass="mapStatus.statusClass"
                />
              </div>
              <transfer-unit-log v-if="transferUnitId" />
            </div>
            <!-- !SECTION - Title -->

            <!-- SECTION -  Info box when status failed -->
            <div v-if="transferUnitStatus === 'failed'" class="info-box">
              <div>
                <!-- triangle exclamation icon -->
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <!-- Triangle -->
                  <path
                    d="M10.29 3.86L2.82 16.86C2.04 18.22 3.02 20 4.53 20H19.47C20.98 20 21.96 18.22 21.18 16.86L13.71 3.86C12.95 2.54 11.05 2.54 10.29 3.86Z"
                    stroke="#BE3E4B"
                    stroke-width="1.6"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />

                  <!-- Exclamation -->
                  <line
                    x1="12"
                    y1="8"
                    x2="12"
                    y2="13"
                    stroke="#BE3E4B"
                    stroke-width="1.8"
                    stroke-linecap="round"
                  />

                  <!-- Dot -->
                  <circle cx="12" cy="16.5" r="1" fill="#BE3E4B" />
                </svg>
              </div>
              <div>
                <span class="info-box-title"
                  >Transisi Entitas Unit Gagal Dialihkan</span
                >
                <span class="info-box-desc"
                  >Terjadi kendala sistem saat memproses pengalihan ini. Jika
                  Anda ingin memperbaiki atau melanjutkan proses, silahkan
                  hubungi <strong>Tim Tech</strong>.</span
                >
              </div>
            </div>
            <!-- !SECTION - Info box when status failed -->

            <!-- SECTION -  Page Content -->
            <div>
              <!-- Tabs -->
              <div class="tabs-section">
                <div class="tab-list">
                  <div
                    v-for="tab in tabs"
                    :key="tab.id"
                    :class="['tab', { active: activeTab === tab.id }]"
                    @click="handleClickTab(tab.id)"
                  >
                    <div
                      v-if="disabledNotDraft && tab.id !== 'verification'"
                      class="check-circle"
                    >
                      <img
                        src="~@/assets/icon/check-white.svg"
                        alt="check-icon"
                      />
                    </div>
                    <span>{{ tab.label }}</span>
                  </div>
                </div>
              </div>

              <transfer-detail v-if="activeTab === 'detail'" />
              <transfer-verification v-if="activeTab === 'verification'" />
              <payment-preview v-if="activeTab === 'paymentPreview'" />
            </div>
            <!-- !SECTION -  Page Content -->

            <!-- SECTION -  Action Button -->
            <!-- Action Button has 3 States 
            1. Draft/create
            2. Verification/ready
            3. Failed
           -->
            <div class="verification-action-btn">
              <div class="back-section" @click="goToTransferList">
                <i class="fas fa-arrow-left"></i>
                <span>Kembali ke Daftar Pembaruan Order</span>
              </div>
              <component-guard
                :requiredPermissions="['28.2', '28.3', '28.4']"
                operator="OR"
              >
                <div
                  v-if="
                    transferUnitStatus === 'draft' ||
                    lesseeTransferId === 'create'
                  "
                  class="action-section"
                >
                  <component-guard :requiredPermissions="['28.4']">
                    <button
                      v-if="transferUnitStatus === 'draft'"
                      @click="deleteDraft"
                      class="btn btn-danger-outline"
                    >
                      Hapus Draft
                    </button>
                  </component-guard>
                  <component-guard
                    v-if="lesseeTransferId === 'create'"
                    :requiredPermissions="['28.2']"
                  >
                    <button
                      @click="triggerSaveDetail('draft')"
                      class="btn btn-outline"
                    >
                      Simpan Draft
                    </button>
                  </component-guard>
                  <component-guard v-else :requiredPermissions="['28.3']">
                    <button
                      @click="triggerSaveDetail('draft')"
                      class="btn btn-outline"
                    >
                      Simpan Draft
                    </button>
                  </component-guard>
                  <button
                    v-if="activeTab === 'detail'"
                    class="btn btn-primary"
                    @click="continueToPaymentPreview"
                  >
                    Lanjut ke Preview Skema Pembayaran
                  </button>
                  <component-guard
                    :requiredPermissions="['28.3']"
                    v-if="activeTab === 'paymentPreview'"
                  >
                    <button
                      class="btn btn-primary"
                      @click="triggerContinueToVerify"
                    >
                      Lanjut ke Verifikasi
                    </button>
                  </component-guard>
                </div>

                <div
                  v-if="
                    transferUnitStatus === 'verification' ||
                    transferUnitStatus === 'ready'
                  "
                  class="action-section"
                >
                  <component-guard :requiredPermissions="['28.4']">
                    <button
                      class="btn btn-danger-outline"
                      @click="cancelTransfer"
                    >
                      <i class="fas fa-times"></i>
                      Batalkan Pengajuan
                    </button>
                  </component-guard>

                  <component-guard :requiredPermissions="['28.3']">
                    <button
                      class="btn btn-primary-outline"
                      @click="returnToDraft"
                    >
                      <i class="fas fa-undo-alt"></i>
                      Kembalikan ke Draft
                    </button>
                  </component-guard>
                </div>

                <div
                  v-if="
                    transferUnitStatus === 'failed' &&
                    canReprocessLesseeTransfer
                  "
                  class="action-section"
                >
                  <button
                    class="btn btn-primary-outline"
                    @click="reprocess"
                    style="width: fit-content"
                  >
                    <img src="~@/assets/icon/refresh-blue.svg" alt="Refresh" />
                    Proses Ulang
                  </button>
                </div>
              </component-guard>
            </div>
            <!-- !SECTION -  Action Button -->
          </div>
        </div>
      </component-guard>
    </LoadingScreen>
  </Wrapper>
</template>

<script>
import { Wrapper, LoadingScreen } from "../Layout";

import TransferDetail from "./FormTabs/Detail/Detail.vue";
import TransferVerification from "./FormTabs/Verification/Verification.vue";
import PaymentPreview from "./FormTabs/PaymentPreview/PaymentPreview.vue";

import TransferUnitLog from "./CustomComponent/TransferUnitLog.vue";
import StatusBadge from "./CustomComponent/StatusBadge.vue";

import mixin from "./mixin.js";

import { ComponentGuard, permissionMixin } from "../Permissions";

import { mapGetters, mapActions, mapMutations } from "vuex";
import { alert } from "../../utils";

export default {
  name: "FormRentAdjustment",
  components: {
    Wrapper,
    LoadingScreen,
    TransferDetail,
    TransferVerification,
    TransferUnitLog,
    StatusBadge,
    ComponentGuard,
    PaymentPreview,
  },
  mixins: [mixin, permissionMixin],
  data() {
    return {
      lesseeTransferId: this.$route.params.id,
      canReprocessLesseeTransfer:
        localStorage.getItem("reProcessLease") === "true",
      activeTab: "detail",
      customBreadcrumb: [
        {
          name: "Pembaruan Order",
          path: "/rent-adjustment",
          link: "/rent-adjustment?orderId=&productCode=&leaseType=&status=&firstLessee=&targetLessee=&dateEfectiveTo=&dateEfectiveFrom=&itemPerPage=10&page=1",
          query: {
            orderId: "",
            productCode: "",
            leaseType: "",
            status: "",
            firstLessee: "",
            targetLessee: "",
            status: "",
            dateEfectiveTo: "",
            dateEfectiveFrom: "",
            itemPerPage: 10,
            page: 1,
          },
        },
        {
          name: "Buat Baru",
        },
      ],
    };
  },
  computed: {
    ...mapGetters("rentAdjustment", {
      currentStep: "getCurrentStep",
      getFormData: "getFormData",
      getLoading: "getLoading",
      getLoadingGetDetail: "getLoadingGetDetail",
      transferUnitId: "getId",
      transferUnitStatus: "getStatus",
      ltCode: "getLtCode",
      disabledNotDraft: "getDisabledNotDraft",
      isNewCompanyFormChanged: "getIsNewCompanyFormChanged",
      getType: "getType",
      getPaymentSchema: "getPaymentSchema",
    }),
    pageType() {
      return this.$route.query.type || this.getType;
    },
    pageTitle() {
      return this.pageType === "change-name"
        ? "Perubahan Nama Lessee"
        : "Pengalihan Unit";
    },
    mapStatus() {
      const statusClassMapping = {
        draft: "black",
        verification: "yellow",
        cancelled: "black",
        executed: "green",
        ready: "blue",
        failed: "red",
        processing: "orange",
      };
      const statusTextMapping = {
        draft: "Draft",
        verification: "Dalam Verifikasi",
        cancelled: "Dibatalkan",
        executed: "Selesai",
        ready: "Siap Dialihkan",
        failed: "Gagal Dialihkan",
        processing: "Sedang Dialihkan",
      };
      return {
        statusClass: statusClassMapping[this.transferUnitStatus],
        status: statusTextMapping[this.transferUnitStatus],
      };
    },
    tabs() {
      return [
        { id: "detail", label: "Detail" },
        { id: "paymentPreview", label: "Preview Skema Pembayaran" },
        { id: "verification", label: "Verifikasi" },
      ];
    },
    unselectTargetUnits() {
      const allEmptyTargetUnit = [];
      for (const unit of this.getFormData.units) {
        const currentEmptyTargetUnit = { orderCode: "", items: [] };
        if (unit.selected && !unit.targetUnit.orderCode) {
          currentEmptyTargetUnit.orderCode = unit.orderCode;
        }
        for (const child of unit.children) {
          if (child.selected && !child.targetUnit.orderCode) {
            currentEmptyTargetUnit.items.push(child.orderCode);
          }
        }
        if (currentEmptyTargetUnit.items.length > 0) {
          allEmptyTargetUnit.push(currentEmptyTargetUnit);
        }
      }
      return allEmptyTargetUnit;
    },
    isPreviewPaymentSchemaHasNonMonthlyTag() {
      if (!this.getPaymentSchema) return false;
      for (const paymentSchema of this.getPaymentSchema) {
        if (paymentSchema.leaseTag) {
          if (!paymentSchema.leaseTag.tag.includes(this.tagMonthly)) {
            return true;
          }
        }
        if (
          !paymentSchema.leaseTag &&
          (!paymentSchema.billingPer ||
          paymentSchema.billingPer !== "Monthly Invoice")
        ) {
          return true;
        }
      }

      return false;
    },
  },
  methods: {
    ...mapActions("rentAdjustment", [
      "saveDetail",
      "getDetailTransferLessee",
      "updateStatusTransferUnit",
      "getVerificationLog",
      "reprocessTransferUnitExecution",
      "fetchPaymentPreviewSchema",
    ]),
    ...mapMutations("rentAdjustment", {
      setType: "SET_TYPE",
    }),

    handleClickTab(tab) {
      if (tab === "paymentPreview") {
        this.continueToPaymentPreview();
        return;
      }
      if (
        tab === "verification" &&
        (this.transferUnitStatus === "draft" || !this.transferUnitStatus)
      ) {
        if (!this.transferUnitStatus && this.activeTab === "detail") return;
        if (!this.hasPermission({ requiredPermissions: ["28.3"] })) return;
        this.triggerContinueToVerify();
        return;
      }
      this.activeTab = tab;
    },
    checklistItem(title, subtitle, verticalLine = true) {
      return `
      <div style="display:flex; align-items:flex-start; gap:12px; margin-bottom:${
        verticalLine ? "12px" : "0px"
      };">
      <div style="display:flex; flex-direction:column; align-items:center; flex-shrink:0;">
        <div style="width:20px; height:20px; border-radius:50%; background-color:#2f6ff0; display:flex; align-items:center; justify-content:center;">
          <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
            <path d="M2.5 6.2L4.8 8.5L9.5 3.5" stroke="#ffffff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </div>
        ${
          verticalLine
            ? '<div style="width:2px; flex:1; background-color:#d7dae0; margin-top:8px; min-height:26px;"></div>'
            : ""
        }
      </div>
      <div style="padding-bottom:8px;">
        <div style="font-size:14.5px; font-weight:700; color:#1f2430; margin-bottom:4px;">${title}</div>
        <div style="font-size:12.5px; color:#8a8f99; line-height:1.5;">${subtitle}</div>
      </div>
    </div>
      `;
    },
    triggerContinueToVerify() {
      if (
        !this.chooseUnitCompleted ||
        !this.effectiveDateCompleted ||
        !this.companyInfoCompleted
      ) {
        if (!this.companyTargetDataCompleted) {
          return alert().fire({
            icon: "error",
            title: "Formulir Lessee Target Belum Lengkap",
            text: "Pastikan semua data pada formulir Lessee Target sudah lengkap sebelum melanjutkan ke preview skema pembayaran.",
          });
        }
        return alert().fire({
          icon: "error",
          title: "Formulir Belum Lengkap",
          text: "Pastikan semua langkah sudah lengkap sebelum melanjutkan ke verifikasi.",
        });
      }

      if (this.unselectTargetUnits.length > 0) {
        return alert().fire({
          icon: "error",
          title: "Formulir Belum Lengkap",
          text: "Pastikan semua unit yang dipilih sudah memiliki target unit.",
          html: `
            <div style="text-align: left; margin-top: 12px; padding-left: 20px;">
              <span style="color: #6b7280; font-size: 16px;">Pastikan semua unit yang sudah dipilih berikut sudah memiliki target unit.</span>
              <ul style="padding-left: 20px; color: #6b7280; font-size: 14px; margin-top: 8px;">
                ${this.unselectTargetUnits
                  .map(
                    (unit) => `
                  <li>
                    <strong>${unit.orderCode}</strong>
                    <ul>
                      ${unit.items.map((item) => `<li>${item}</li>`).join("")}
                    </ul>
                  </li>
                `
                  )
                  .join("")}
              </ul>
            </div>
          `,
        });
      }

      if (this.isPreviewPaymentSchemaHasNonMonthlyTag) {
        return alert().fire({
          icon: "error",
          title: "Tag Order Baru Tidak Sesuai",
          text: "Pastikan semua order menggunakan tag Billed Per sebagai 'Monthly Invoice' sebelum melanjutkan ke verifikasi.",
        });
      }

      alert()
        .fire({
          title: "Lanjut ke Verifikasi?",
          icon: "warning",
          iconColor: "#f59e0b",
          showCancelButton: true,
          confirmButtonText: "Ya, Lanjut ke Verifikasi",
          cancelButtonText: "Cek Ulang",
          confirmButtonColor: "#2563eb",
          cancelButtonColor: "#ffffff",
          width: 560,
          padding: "2rem",
          customClass: {
            cancelButton: "swal-cancel-custom-lt",
            confirmButton: "swal-confirm-custom-lt",
            htmlContainer: "swal-html-custom-lt",
            icon: "swal-icons-custom-lt",
          },
          html: `
          <div style="font-size: 15px; color: #2E3646; line-height: 1.5; margin: 20px 0px;">
            Melanjutkan proses ini akan menghentikan (cut-off) masa sewa 
            lama dan membuka komitmen perjanjian sewa baru. 
          </div>
          <div style="
            background: #fffbeb;
            border: 1px solid #fde68a;
            border-radius: 8px;
            padding: 12px 16px;
            margin-bottom: 16px;
            font-size: 14px;
            color: #92400e;
            text-align: center;
            margin-top: 20px;
          ">
            Pastikan seluruh data sudah sepenuhnya akurat karena data tidak dapat 
            diubah kembali setelah dikirim untuk verifikasi.
          </div>

          <div 
            style="
              max-width:560px; 
              background-color:#f4f5f8; 
              border:1px solid #e5e7eb; 
              border-radius:14px; 
              padding:24px 24px 20px 24px;
              text-align:left;
            ">
            ${this.checklistItem(
              "Pilih Target Lessee",
              "Pastikan profil Lessee Awal telah dialihkan ke Lessee Baru dengan benar."
            )}
            ${this.checklistItem(
              "Daftar Unit & Pengalihan Deposit",
              "Pastikan daftar unit yang ikut serta dan pengalihan deposit sudah sesuai."
            )}
            ${this.checklistItem(
              "Tanggal Efektif",
              "Perjanjian sewa baru akan aktif otomatis pada tanggal yang dipilih."
            )}
            ${this.checklistItem(
              "Preview Skema Pembayaran",
              "Perubahan tag order saat preview skema pembayaran sudah sesuai.",
              false
            )}
          </div>
        `,
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.triggerSaveDetail("verify");
          }
        });
    },
    goToTransferList() {
      this.$router.push({
        path: "/rent-adjustment",
        query: {
          orderId: "",
          productCode: "",
          leaseType: "",
          status: "",
          firstLessee: "",
          targetLessee: "",
          dateFrom: "",
          dateTo: "",
          itemPerPage: 10,
          page: 1,
        },
      });
    },

    async cancelTransfer() {
      const alertResponse = await alert().fire({
        title: "Batalkan Seluruh Pengajuan?",
        icon: "warning",
        iconColor: "#FF3444",
        showCancelButton: true,
        confirmButtonText: "Ya, Batalkan",
        cancelButtonText: "Tutup",
        width: 600,
        padding: "2rem",
        input: "textarea",
        inputLabel: "Alasan Pembatalan",
        inputPlaceholder: "Tulis alasan pembatalan ini...",
        inputValidator: (value) => {
          if (!value || value.length < 3)
            return "Mohon untuk memberikan penjelasan yang lebih mendetail.";
        },
        customClass: {
          cancelButton: "swal-cancel-custom-lt",
          htmlContainer: "swal-html-custom-lt",
          confirmButton: "swal-confirm-custom-cancel-application-lt",
          actions: "swal-actions-custom-lt",
          input: "swal-input-custom-lt",
          inputLabel: "swal-input-label-custom-lt",
          validationMessage: "swal-validation-message-custom-lt",
        },
        html: `
          <p style="
            font-size: 18px; 
            color: #2E3646; 
            margin: 20px 0"
          >
            Status pengajuan ini akan berubah menjadi Cancelled dan tidak dapat dilanjutkan kembali.
          </p>
          <div style="
            background: #FFF1F2;
            border: 1px solid #FFC5CA;
            border-radius: 8px;
            padding: 12px 16px;
            margin-bottom: 16px;
            font-size: 14px;
            color: #A50F1B;
            text-align: center;
            margin-top: 20px;
          ">
            Seluruh data ini akan tetap ada di riwayat namun tidak dapat diproses lagi.
          </div>
        `,
      });

      if (alertResponse.isConfirmed) {
        const response = await this.updateStatusTransferUnit({
          transferUnitId: this.transferUnitId,
          status: "cancelled",
          reason: alertResponse.value,
        });
        if (response.isSuccess) {
          await this.getDetailTransferLessee(this.transferUnitId);
        }
      }
    },

    async deleteDraft() {
      const alertResponse = await alert({
        confirmButtonColor: "red",
      }).fire({
        title: "Hapus Draft?",
        icon: "warning",
        iconColor: "#FF3444",
        text: "Menghapus draft akan menghilangkan seluruh data yang telah Anda isi secara permanen dan tidak dapat dipulihkan.",
        showCancelButton: true,
        confirmButtonText: "Hapus Draft",
        cancelButtonText: "Batal",
        cancelButtonColor: "#ffffff",
      });

      if (alertResponse.isConfirmed) {
        const response = await this.updateStatusTransferUnit({
          transferUnitId: this.transferUnitId,
          status: "deleted",
        });
        if (response.isSuccess) {
          await alert().fire({
            icon: "success",
            title: "Draft berhasil dihapus",
          });
          this.goToTransferList();
        } else {
          alert().fire({
            icon: "error",
            title: "Gagal Menghapus Draft",
            text: response.message,
          });
        }
      }
    },

    async reprocess() {
      try {
        const response = await this.reprocessTransferUnitExecution({
          transferUnitId: this.transferUnitId,
        });
        if (response.isSuccess) {
          window.location.reload();
        } else {
          alert().fire({
            icon: "error",
            title: "Gagal Memproses Ulang",
            text: response.message,
          });
        }
      } catch (error) {
        console.error(
          "Error occurred while reprocessing transfer unit execution:",
          error
        );
      }
    },

    async returnToDraft() {
      const alertResponse = await alert().fire({
        title: "Kembalikan ke Draft?",
        icon: "warning",
        iconColor: "#f59e0b",
        showCancelButton: true,
        confirmButtonText: "Kembalikan ke Draft",
        cancelButtonText: "Lanjutkan Verifikasi",
        confirmButtonColor: "#2563eb",
        cancelButtonColor: "#ffffff",
        width: 500,
        padding: "2rem",
        customClass: {
          cancelButton: "swal-cancel-custom-lt",
          confirmButton: "swal-confirm-custom-lt",
          htmlContainer: "swal-html-custom-lt",
        },
        html: `
          <div style="
            background: #fffbeb;
            border: 1px solid #fde68a;
            border-radius: 8px;
            padding: 12px 16px;
            margin-bottom: 16px;
            font-size: 14px;
            color: #92400e;
            text-align: center;
            margin-top: 20px;
          ">
            Lakukan ini hanya jika ada kesalahan data fatal yang perlu diubah sebelumnya.
          </div>
        `,
      });

      if (alertResponse.isConfirmed) {
        const response = await this.updateStatusTransferUnit({
          transferUnitId: this.transferUnitId,
          status: "draft",
        });

        if (response.isSuccess) {
          alert()
            .fire({
              icon: "success",
              title: "Berhasil Dikembalikan ke Draft",
              text: "Perubahan sewa berhasil dikembalikan ke draft.",
            })
            .then(async () => {
              await this.getDetailTransferLessee(this.transferUnitId);
              this.activeTab = "detail";
            });
        } else {
          alert().fire({
            icon: "error",
            title: "Gagal Mengubah Status",
            text: response.message,
          });
        }
      }
    },

    async triggerSaveDetail(type) {
      if (this.isNewCompanyFormChanged) {
        const alertResponseFormLesseeTarget = await alert().fire({
          icon: "warning",
          title: "Formulir Lessee Target Belum Disimpan",
          text: "Apakah Anda ingin melanjutkan tanpa menyimpan perubahan pada formulir Lessee Target?",
          showCancelButton: true,
          confirmButtonText: "Ya, Lanjutkan",
          cancelButtonText: "Kembali",
        });

        if (!alertResponseFormLesseeTarget.isConfirmed) {
          return;
        }
      }

      if (type === "draft" && !this.getFormData.companyIdOriginal) {
        return alert().fire({
          icon: "error",
          title: "Formulir Belum Lengkap",
          text: "Pastikan lessee awal sudah dipilih.",
        });
      }
      const response = await this.saveDetail({
        value: this.getFormData,
        type: type || "draft",
      });
      if (response.isSuccess) {
        if (type === "verify") {
          await alert().fire({
            icon: "success",
            title: "Berhasil Lanjut ke Verifikasi",
            text: "Detail transfer entitas unit berhasil disimpan dan dilanjutkan ke tahap Verifikasi",
          });
          this.$router.push({
            path: `/rent-adjustment/${this.transferUnitId}?tab=verification`,
          });

          return location.reload();
        }
        alert()
          .fire({
            icon: "success",
            title: "Success",
            text: "Perubahan Nama Lessee berhasil disimpan sebagai Draft",
          })
          .then(() => {
            this.goToTransferList();
          });
      } else {
        alert().fire({
          icon: "error",
          title: "Error",
          text: response.message,
        });
      }
    },

    async continueToPaymentPreview() {
      if (
        !this.chooseUnitCompleted ||
        !this.effectiveDateCompleted ||
        !this.companyInfoCompleted
      ) {
        if (!this.companyTargetDataCompleted) {
          return alert().fire({
            icon: "error",
            title: "Formulir Lessee Target Belum Lengkap",
            text: "Pastikan semua data pada formulir Lessee Target sudah lengkap sebelum melanjutkan ke preview skema pembayaran.",
          });
        }
        return alert().fire({
          icon: "error",
          title: "Formulir Belum Lengkap",
          text: "Pastikan semua langkah sudah lengkap sebelum melanjutkan ke preview skema pembayaran.",
        });
      }
      if (this.transferUnitStatus === "draft" || !this.transferUnitStatus) {
        const response = await this.fetchPaymentPreviewSchema();
        if (!response.isSuccess) {
          return alert().fire({
            icon: "error",
            title: "Error",
            text: response.message,
          });
        }
      }

      this.activeTab = "paymentPreview";
    },
  },
  watch: {
    ltCode: {
      handler(newValue) {
        if (newValue) {
          this.customBreadcrumb[1].name = `ID Pembaruan: (${newValue})`;
        }
      },
    },
  },
  async mounted() {
    if (
      !this.hasPermission({
        requiredPermissions: ["28.1"],
        redirectUrl: "/home",
      })
    ) {
      return;
    }
    if (this.lesseeTransferId !== "create") {
      const { isSuccess, message, data } = await this.getDetailTransferLessee(
        this.lesseeTransferId
      );
      if (
        isSuccess &&
        data.status !== "draft" &&
        this.$route.query.tab == "verification"
      ) {
        this.activeTab = "verification";
      }
      if (!isSuccess) {
        await alert().fire({
          icon: "error",
          title: "Error",
          text: message,
        });
        this.goToTransferList();
      }
    } else {
      if (!this.pageType) {
        this.goToTransferList();
        return;
      }
      this.setType(this.pageType);
    }
  },
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.btn {
  font-size: 14px;
  padding: 10px 20px;
  border-radius: 8px;
}

.btn-outline {
  border: 1px solid #247fec;
  background-color: #ffffff;
  color: #247fec;
}

.form-rent-adjustment-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #ffffff;
}

.breadcrumb {
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 16px;
  font-weight: 500;
}

.content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 32px 72px;
  overflow-y: auto;
}

.title-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.title-section h1 {
  font-size: 20px;
  font-weight: 600;
  color: #2e3646;
  text-transform: capitalize;
}

.tabs-section {
  background-color: #ffffff;
  border: 1px solid #d1d9e2;
  border-bottom: none;
  border-radius: 8px 8px 0 0;
}

.tab-list {
  display: flex;
  gap: 40px;
  padding: 0 20px;
  border-bottom: 1px solid #d1d9e2;
}

.tab {
  padding: 16px 20px;
  font-size: 16px;
  font-weight: 600;
  color: #2e3646;
  cursor: pointer;
  text-transform: capitalize;
  border-bottom: 2px solid transparent;
  opacity: 0.4;
  transition: all 0.3s ease;
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
}

.tab.active {
  opacity: 1;
  border-bottom-color: #2e3646;
}

.form-section {
  background-color: #ffffff;
  border: 1px solid #d1d9e2;
  border-radius: 0 0 8px 8px;
  padding: 20px;
}

/* Responsive */
@media (max-width: 768px) {
  .content {
    padding: 20px;
  }

  .tab-list {
    gap: 20px;
  }
}

.verification-action-btn {
  height: 56px;
  padding: 0 16px;

  display: flex;
  align-items: center;
  justify-content: space-between;
}

.back-section {
  display: flex;
  align-items: center;
  gap: 8px;

  font-size: 14px;
  font-weight: 500;
  color: #374151;

  cursor: pointer;
}

.back-section i {
  font-size: 13px;
}

.action-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.verification-action-btn .btn {
  height: 36px;
  padding: 0 16px;

  display: flex;
  align-items: center;
  gap: 8px;

  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;

  cursor: pointer;
}

.verification-action-btn .btn i {
  font-size: 12px;
}

.btn-danger-outline {
  color: #ef4444;
  border: 1px solid #ef4444;
}

.btn-danger-outline:hover {
  background: #fef2f2;
}

.btn-primary-outline {
  color: #2563eb;
  border: 1px solid #2563eb;
}

.btn-primary-outline:hover {
  background: #eff6ff;
}

.check-circle {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #247fec;
  background-color: #247fec;
  display: flex;
  justify-content: center;
  align-items: center;
}

.info-box {
  color: #be3846;
  background-color: #fdf3f3;
  border: 1px solid #f0b1b2;
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  align-items: center;
  border-radius: 8px;
}

.info-box-title {
  font-size: 14px;
  font-weight: 600;
}

.info-box-desc {
  font-size: 12px;
  display: block;
  margin-top: 4px;
}

.blue-info-box {
  color: #1d54b0;
  background-color: #eff8ff;
  border: 1px solid #92d4fe;
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
}

.blue-info-box p {
  margin: 0;
}
</style>

<style>
.swal-validation-message-custom-lt {
  font-size: 12px;
  color: #ef4444;
  margin-top: 4px;
  background: transparent;
  justify-content: flex-start;
  padding-top: 0;
}

.swal-validation-message-custom-lt::before {
  display: none;
}

.swal-input-custom-lt {
  margin: 0;
  outline: none !important;
  box-shadow: none !important;
  border: 1px solid #cfd3d9;
  border-radius: 8px;
  font-size: 16px;
}

.swal-input-label-custom-lt {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 8px;
  width: 100%;
  text-align: left;
  justify-content: flex-start;
}

.swal-actions-custom-lt {
  display: flex;
  gap: 8px;
  justify-content: right;
  width: 100%;
}

.swal-confirm-custom-cancel-application-lt {
  color: #ffffff;
  font-weight: 600;
  border-radius: 8px;
  background: #ff3444;
  border: 1px solid #ff3444;
  font-size: 16px;
  padding: 12px 24px;
  outline: none !important;
}

.swal-cancel-custom-lt {
  color: #2e3646;
  font-weight: 600;
  border-radius: 6px;
  background: #ffffff;
  border: 1px solid #cfd3d9;
  font-size: 16px;
  padding: 12px 24px;
  margin-right: 8px;
}
.swal-confirm-custom-lt {
  color: #ffffff;
  font-weight: 600;
  border-radius: 8px;
  background: #247fec;
  border: 1px solid #247fec;
  font-size: 16px;
  padding: 12px 24px;
  outline: none !important;
}
.swal-cancel-custom-lt:hover {
  background: #f3f4f6;
}
.swal-html-custom-lt {
  padding: 0 !important;
}
.swal-icons-custom-lt {
  margin-top: 0 !important;
}
</style>



// WEBPACK FOOTER //
// src/components/RentAdjustment/FormRentAdjustment.vue